<script lang="ts">
  import { untrack } from "svelte";
  import { SvelteMap } from "svelte/reactivity";
  import { calculateCreditTopUp, MIN_TOP_UP_RUBLES, MAX_TOP_UP_RUBLES } from "@mars-host/shared";
  import { api, ApiError, type CreditTopUp, type CreditTopUpConfig } from "@/api";
  import { token, user } from "@/stores/session";
  import { refreshAccount } from "@/stores/dashboard";
  import { formatNumber } from "@/utils/format";
  import FormField from "@/components/FormField.svelte";
  import TextInput from "@/components/TextInput.svelte";

  const { oncredited }: { oncredited?: () => void } = $props();
  let amount = $state("250");
  let config = $state<CreditTopUpConfig | null>(null);
  let invoices = $state<CreditTopUp[]>([]);
  let loading = $state(true);
  let creating = $state(false);
  let checking = $state<string[]>([]);
  let error = $state("");
  let historyError = $state("");
  let latestInvoice = $state<string | null>(null);
  let retry = $state(0);
  let sessionController: AbortController | null = null;
  let pendingRequest: { amountRubles: number; idempotencyKey: string } | null = null;
  const min = $derived(config?.minRubles ?? MIN_TOP_UP_RUBLES);
  const max = $derived(config?.maxRubles ?? MAX_TOP_UP_RUBLES);
  const amountRubles = $derived(Number(amount));
  const amountValid = $derived(
    /^\d+$/.test(amount) &&
      Number.isSafeInteger(amountRubles) &&
      amountRubles >= min &&
      amountRubles <= max,
  );
  const quote = $derived(amountValid ? calculateCreditTopUp(amountRubles) : null);
  const latest = $derived(invoices.find((invoice) => invoice.id === latestInvoice));
  function checkoutUrl(invoice: CreditTopUp): string | null {
    if (invoice.creditedAt) {
      return null;
    }
    if (!invoice.checkoutUrl) {
      return null;
    }
    try {
      const url = new URL(invoice.checkoutUrl);
      return url.protocol === "https:" &&
        ["pay.heleket.com", "new-pay.heleket.com"].includes(url.hostname) &&
        !url.port &&
        !url.username &&
        !url.password
        ? url.href
        : null;
    } catch {
      return null;
    }
  }

  function unfinished(invoice: CreditTopUp): boolean {
    return (
      !invoice.creditedAt &&
      !["cancel", "fail", "system_fail", "refund_paid"].includes(invoice.status)
    );
  }

  function statusLabel(invoice: CreditTopUp): string {
    if (invoice.creditedAt) {
      return "Кредиты зачислены";
    }
    const labels: Record<string, string> = {
      creating: "Создаём счёт",
      pending: "Ожидает оплаты",
      process: "Платёж обрабатывается",
      check: "Проверяем платёж",
      confirm_check: "Ожидаем подтверждения",
      paid: "Подтверждаем зачисление",
      paid_over: "Подтверждаем зачисление",
      cancel: "Срок оплаты истёк",
      fail: "Платёж не завершён",
      system_fail: "Ошибка платежа",
      wrong_amount: "Сумма платежа не совпала — открой счёт",
      locked: "Платёж на проверке",
      refund_process: "Возврат обрабатывается",
      refund_fail: "Ошибка возврата",
      refund_paid: "Платёж возвращён",
    };
    return labels[invoice.status] ?? "Проверяем статус платежа";
  }

  function date(value: string): string {
    return new Date(value).toLocaleString("ru-RU", { dateStyle: "short", timeStyle: "short" });
  }

  function requestStorageKey(): string {
    return `mars-top-up-request:${$user?.id ?? ""}`;
  }

  function restoreRequest(): void {
    pendingRequest = null;
    try {
      const raw: unknown = JSON.parse(sessionStorage.getItem(requestStorageKey()) ?? "null");
      if (
        raw &&
        typeof raw === "object" &&
        "amountRubles" in raw &&
        "idempotencyKey" in raw &&
        typeof raw.amountRubles === "number" &&
        Number.isSafeInteger(raw.amountRubles) &&
        typeof raw.idempotencyKey === "string" &&
        /^[0-9a-f-]{36}$/i.test(raw.idempotencyKey)
      ) {
        pendingRequest = { amountRubles: raw.amountRubles, idempotencyKey: raw.idempotencyKey };
        amount = String(raw.amountRubles);
      }
    } catch {
      // A blocked browser store does not prevent paying in the current view.
    }
  }

  function saveRequest(): void {
    try {
      if (pendingRequest) {
        sessionStorage.setItem(requestStorageKey(), JSON.stringify(pendingRequest));
      } else {
        sessionStorage.removeItem(requestStorageKey());
      }
    } catch {
      // The in-memory key still makes a retry safe while the view is open.
    }
  }

  async function publish(next: CreditTopUp[], signal: AbortSignal): Promise<void> {
    if (signal.aborted) {
      return;
    }
    const credited = next.some(
      (invoice) =>
        invoice.creditedAt &&
        !invoices.some((previous) => previous.id === invoice.id && previous.creditedAt),
    );
    // A background check may finish after creation or a manual check. Keep invoices
    // added while it was in flight and never roll back confirmed ledger crediting.
    const merged = new SvelteMap(invoices.map((invoice) => [invoice.id, invoice]));
    for (const invoice of next) {
      const previous = merged.get(invoice.id);
      merged.set(invoice.id, previous?.creditedAt ? previous : invoice);
    }
    invoices = [...merged.values()]
      .sort((left, right) => Date.parse(right.createdAt) - Date.parse(left.createdAt))
      .slice(0, 20);
    if (credited) {
      await refreshAccount(signal);
      if (!signal.aborted) {
        oncredited?.();
      }
    }
  }

  $effect(() => {
    const currentToken = $token;
    const userId = $user?.id;
    void retry;
    const controller = new AbortController();
    sessionController = controller;
    config = null;
    invoices = [];
    error = "";
    historyError = "";
    loading = true;
    creating = false;
    checking = [];
    latestInvoice = null;
    untrack(restoreRequest);
    let active = false;
    async function load(): Promise<void> {
      if (!currentToken || !userId) {
        return;
      }
      try {
        const [settings, rows] = await Promise.all([
          api.topUpConfig(currentToken, controller.signal),
          api.topUps(currentToken, controller.signal),
        ]);
        if (controller.signal.aborted) {
          return;
        }
        config = settings;
        await publish(rows, controller.signal);
      } catch (cause) {
        if (!controller.signal.aborted) {
          error = cause instanceof Error ? cause.message : "Не удалось загрузить пополнение";
        }
      } finally {
        if (!controller.signal.aborted) {
          loading = false;
        }
      }
    }
    async function poll(): Promise<void> {
      if (!currentToken || controller.signal.aborted || active || document.hidden) {
        return;
      }
      active = true;
      try {
        const rows = await api.topUps(currentToken, controller.signal);
        // Bound provider requests and rotate invoices so older pending payments are also checked.
        const pending = rows.filter(unfinished);
        const selected = pending.length
          ? pending[Math.floor(Date.now() / 15000) % pending.length]
          : null;
        if (selected) {
          const updated = await api.checkTopUp(currentToken, selected.id, controller.signal);
          await publish(
            rows.map((row) => (row.id === updated.id ? updated : row)),
            controller.signal,
          );
        } else {
          await publish(rows, controller.signal);
        }
        if (!controller.signal.aborted) {
          historyError = "";
        }
      } catch (cause) {
        if (!controller.signal.aborted) {
          historyError = cause instanceof Error ? cause.message : "Не удалось проверить оплату";
        }
      } finally {
        active = false;
      }
    }
    void load();
    const timer = setInterval(() => void poll(), 15000);
    const focused = () => void poll();
    window.addEventListener("focus", focused);
    return () => {
      controller.abort();
      clearInterval(timer);
      window.removeEventListener("focus", focused);
      if (sessionController === controller) {
        sessionController = null;
      }
    };
  });

  async function create(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    const currentToken = $token;
    const controller = sessionController;
    if (!currentToken || !controller || !config?.enabled || !amountValid || creating) {
      return;
    }
    creating = true;
    error = "";
    if (!pendingRequest || pendingRequest.amountRubles !== amountRubles) {
      pendingRequest = { amountRubles, idempotencyKey: crypto.randomUUID() };
      saveRequest();
    }
    try {
      const invoice = await api.createTopUp(currentToken, pendingRequest, controller.signal);
      if (controller.signal.aborted) {
        return;
      }
      pendingRequest = null;
      saveRequest();
      latestInvoice = invoice.id;
      await publish(
        [invoice, ...invoices.filter((row) => row.id !== invoice.id)].slice(0, 20),
        controller.signal,
      );
    } catch (cause) {
      if (!controller.signal.aborted) {
        if (
          cause instanceof ApiError &&
          cause.status >= 400 &&
          cause.status < 500 &&
          cause.status !== 429
        ) {
          pendingRequest = null;
          saveRequest();
        }
        error =
          cause instanceof Error ? cause.message : "Не удалось создать счёт. Попробуй ещё раз.";
      }
    } finally {
      if (!controller.signal.aborted) {
        creating = false;
      }
    }
  }

  async function check(invoice: CreditTopUp): Promise<void> {
    const currentToken = $token;
    const controller = sessionController;
    if (!currentToken || !controller || checking.includes(invoice.id)) {
      return;
    }
    checking = [...checking, invoice.id];
    historyError = "";
    try {
      const updated = await api.checkTopUp(currentToken, invoice.id, controller.signal);
      await publish(
        invoices.map((row) => (row.id === updated.id ? updated : row)),
        controller.signal,
      );
    } catch (cause) {
      if (!controller.signal.aborted) {
        historyError = cause instanceof Error ? cause.message : "Не удалось проверить оплату";
      }
    } finally {
      if (!controller.signal.aborted) {
        checking = checking.filter((id) => id !== invoice.id);
      }
    }
  }
</script>

<section class="panel-card top-up" aria-labelledby="top-up-title">
  <div class="heading">
    <h2 id="top-up-title">Пополнить кредиты</h2>
    <p>Выбери сумму. Чем больше пополнение, тем больше кредитов в подарок — до 20% сверху.</p>
  </div>
  {#if loading}
    <p class="muted" role="status">Загружаем способы пополнения…</p>
  {:else}
    {#if config?.enabled}
      <div class="top-up-layout">
        <form onsubmit={create}>
          <FormField
            label="Сумма в рублях"
            hint={`От ${formatNumber(min)} до ${formatNumber(max)} ₽, без копеек.`}
          >
            <TextInput
              bind:value={amount}
              inputmode="numeric"
              pattern="[0-9]+"
              required
              disabled={creating}
              aria-invalid={!amountValid}
            />
          </FormField>
          <div class="packages" aria-label="Быстрый выбор суммы">
            {#each [100, 250, 500, 1100] as value (value)}
              <button
                type="button"
                class="ghost-button"
                class:selected={amountRubles === value}
                disabled={creating}
                onclick={() => (amount = String(value))}>{formatNumber(value)} ₽</button
              >
            {/each}
          </div>
          {#if quote}
            <p class="total">Получишь <strong>{formatNumber(quote.credits)}</strong> кредитов</p>
            {#if quote.bonusCredits > 0}
              <p class="bonus">
                В том числе {formatNumber(quote.bonusCredits)} в подарок (+{quote.bonusPercent}%)
              </p>
            {:else}
              <p class="muted">10 кредитов за каждый рубль</p>
            {/if}
          {:else}
            <p class="form-error">
              Укажи целую сумму от {formatNumber(min)} до {formatNumber(max)} ₽.
            </p>
          {/if}
          <button class="button" type="submit" disabled={creating || !amountValid}
            >{creating ? "Создаём счёт…" : "Создать счёт"}</button
          >
          <p class="hint">
            Оплата криптовалютой через Heleket. Валюта, сеть и сумма к оплате будут указаны на
            странице счёта. Кредиты поступят после подтверждения платежа.
          </p>
        </form>
        <div class="payment-help">
          <h3>Как пополнить баланс</h3>
          <ol>
            <li>Укажи сумму и создай счёт.</li>
            <li>Открой счёт и следуй инструкции Heleket.</li>
            <li>Вернись сюда — статус обновится автоматически.</li>
          </ol>
          <p class="muted">
            Если платёж ещё подтверждается, повторно платить не нужно. Счёт сохранится в истории
            ниже.
          </p>
        </div>
      </div>
    {:else if config}
      <p class="muted">
        Пополнение недоступно. Способ оплаты появится после настройки платёжного сервиса.
      </p>
    {/if}
    {#if error}
      <p class="form-error" role="alert">{error}</p>
      {#if !config}<button class="ghost-button" type="button" onclick={() => retry++}
          >Повторить загрузку</button
        >{/if}
    {/if}
    {#if latest}
      <div class="current-payment" role="status">
        <p>Счёт на {formatNumber(latest.amountRubles)} ₽: {statusLabel(latest)}</p>
        {#if checkoutUrl(latest)}
          <a class="button" href={checkoutUrl(latest)} target="_blank" rel="noopener noreferrer"
            >Перейти к оплате ↗</a
          >
        {/if}
      </div>
    {/if}
  {/if}
  <div class="payment-history">
    <h3>История пополнений</h3>
    {#if historyError}<p class="form-error" role="alert">{historyError}</p>{/if}
    {#if !loading && invoices.length === 0}<p class="muted">Здесь появятся созданные счета.</p>{/if}
    <ul>
      {#each invoices as invoice (invoice.id)}
        <li>
          <div>
            <strong
              >{formatNumber(invoice.amountRubles)} ₽ → {formatNumber(invoice.credits)} кр.</strong
            ><small>{date(invoice.createdAt)}</small><span class:credited={!!invoice.creditedAt}
              >{statusLabel(invoice)}</span
            >
          </div>
          <div class="invoice-actions">
            {#if unfinished(invoice) && checkoutUrl(invoice)}<a
                href={checkoutUrl(invoice)}
                target="_blank"
                rel="noopener noreferrer">Открыть счёт ↗</a
              >{/if}
            {#if unfinished(invoice)}<button
                class="ghost-button"
                type="button"
                disabled={checking.includes(invoice.id)}
                onclick={() => void check(invoice)}
                >{checking.includes(invoice.id) ? "Проверяем…" : "Проверить оплату"}</button
              >{/if}
          </div>
        </li>
      {/each}
    </ul>
  </div>
</section>

<style>
  .top-up {
    min-width: 0;
    padding: 1.5rem;
  }
  h2 {
    margin: 0 0 0.8rem;
    font: 500 1.2rem/1.2 var(--font-display);
    text-transform: uppercase;
  }
  h3 {
    margin: 0 0 1rem;
    font-size: 1rem;
  }
  .heading p,
  .muted,
  .hint {
    color: var(--muted);
    line-height: 1.6;
  }
  .top-up-layout {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 2rem;
    margin-top: 1.5rem;
  }
  form {
    display: grid;
    align-content: start;
    gap: 0.8rem;
    min-width: 0;
  }
  form p {
    margin: 0;
  }
  .packages,
  .invoice-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.6rem;
  }
  .ghost-button {
    border: 1px solid var(--line-strong);
    padding: 0.65rem 0.85rem;
  }
  .packages .selected {
    border-color: var(--mars-light);
    color: var(--cream);
  }
  .total {
    padding-top: 0.5rem;
    font-size: 1rem;
  }
  .total strong {
    color: var(--cream);
    font-size: 1.5rem;
  }
  .bonus,
  .credited {
    color: var(--green);
  }
  .hint {
    font-size: 0.75rem;
  }
  .payment-help {
    border-left: 1px solid var(--line);
    padding-left: 2rem;
  }
  ol {
    padding-left: 1.3rem;
    color: var(--muted);
    line-height: 1.7;
  }
  ol li + li {
    margin-top: 0.6rem;
  }
  .current-payment {
    margin-top: 1.5rem;
    padding: 1rem;
    border: 1px solid var(--line-strong);
  }
  .payment-history {
    margin-top: 2rem;
  }
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  ul li {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem 0;
    border-top: 1px solid var(--line);
  }
  ul li > div:first-child {
    display: grid;
    gap: 0.4rem;
  }
  small {
    color: var(--dim);
  }
  .invoice-actions a {
    color: var(--cream);
  }
  @media (max-width: 700px) {
    .top-up-layout {
      grid-template-columns: 1fr;
      gap: 1.5rem;
    }
    .payment-help {
      padding-left: 0;
      padding-top: 1rem;
      border-left: 0;
      border-top: 1px solid var(--line);
    }
    ul li {
      flex-direction: column;
    }
  }
</style>
